import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as ArrowIcon,
} from "@mui/icons-material";
import { STRONG_DROPDOWN_PAPER_SX } from "../../common/dropdownPaper";
import { ExportFormat } from "./sheetExport";

/**
 * One download entry point for the whole preview. The header used to hold a
 * single Download icon; putting a second, similar icon beside it for "export
 * the view" is exactly the pair people confuse, so both live in this menu under
 * headings that say which is which.
 */

interface ExportMenuProps {
  fileName: string;
  fileSizeLabel: string;
  rowCount: number;
  columnCount: number;
  /** Set when the preview itself is showing only part of the file. */
  truncatedRows: number | null;
  busy: boolean;
  onDownloadOriginal: () => void;
  onExportView: (format: ExportFormat) => void;
  onExportAllSheets: () => void;
  onCopyView: () => void;
  sheetCount: number;
  container?: HTMLElement | null;
  zIndex?: number;
}

const ExportMenu: React.FC<ExportMenuProps> = ({
  fileName,
  fileSizeLabel,
  rowCount,
  columnCount,
  truncatedRows,
  busy,
  onDownloadOriginal,
  onExportView,
  onExportAllSheets,
  onCopyView,
  sheetCount,
  container,
  zIndex,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const nothingToExport = rowCount === 0 || columnCount === 0;

  const viewCaption = truncatedRows
    ? `CURRENT VIEW - ${rowCount.toLocaleString()} of the first ${truncatedRows.toLocaleString()} rows x ${columnCount} columns`
    : `CURRENT VIEW - ${rowCount.toLocaleString()} rows x ${columnCount} columns`;

  return (
    <>
      <Button
        size="small"
        startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
        endIcon={<ArrowIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={busy}
        sx={{ textTransform: "none" }}
        aria-haspopup="menu"
      >
        Export
      </Button>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        container={container ?? undefined}
        slotProps={{
          paper: {
            elevation: 0,
            sx: [STRONG_DROPDOWN_PAPER_SX, { maxWidth: 360 }],
          },
        }}
        sx={zIndex != null ? { zIndex } : undefined}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, pt: 1, display: "block", letterSpacing: 0.4 }}
        >
          ORIGINAL FILE
        </Typography>
        <MenuItem
          onClick={() => {
            onDownloadOriginal();
            close();
          }}
        >
          <ListItemText
            primary={`Download ${fileName}`}
            secondary={fileSizeLabel || undefined}
            primaryTypographyProps={{ noWrap: true }}
          />
        </MenuItem>

        <Divider />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, pt: 1, display: "block", letterSpacing: 0.4 }}
        >
          {viewCaption}
        </Typography>

        <MenuItem
          disabled={nothingToExport}
          onClick={() => {
            onExportView("xlsx");
            close();
          }}
        >
          <ListItemText
            primary="Export view as XLSX"
            secondary="Values are written exactly as shown"
          />
        </MenuItem>
        <MenuItem
          disabled={nothingToExport}
          onClick={() => {
            onExportView("csv");
            close();
          }}
        >
          <ListItemText
            primary="Export view as CSV"
            secondary="Excel may reformat IDs and dates on open"
          />
        </MenuItem>
        {sheetCount > 1 && (
          <MenuItem
            onClick={() => {
              onExportAllSheets();
              close();
            }}
          >
            <ListItemText
              primary={`Export all ${sheetCount} sheets as XLSX`}
              secondary="Views applied where you set them; untouched sheets go out whole"
            />
          </MenuItem>
        )}
        <MenuItem
          disabled={nothingToExport}
          onClick={() => {
            onCopyView();
            close();
          }}
        >
          <CopyIcon fontSize="small" sx={{ mr: 1 }} />
          <ListItemText primary="Copy view to clipboard" />
        </MenuItem>

        {nothingToExport && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Nothing to export - no rows match the current filters.
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default ExportMenu;
